// API utility functions with token handling (axios-based)

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { clearAuth, isTokenValid } from './auth';

// Use relative URL to leverage Vite proxy in development
// This avoids browser SSL certificate issues with self-signed certs
const API_BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance
// withCredentials ensures the httpOnly auth_token cookie is sent with every request.
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get current location
const getCurrentLocation = (): Promise<{ latitude: number; longitude: number } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Error getting location:', error);
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  });
};

// Helper function to check if an endpoint should skip authentication
const shouldSkipAuth = (url: string): boolean => {
  if (!url) return false;
  // Check for auth endpoints
  if (url.startsWith('/auth/')) return true;
  // Check for QuickBooks public endpoints (auth-url, auth, callback)
  if (url.startsWith('/quickbooks/auth-url') || 
      url.startsWith('/quickbooks/auth') || 
      url.startsWith('/quickbooks/callback')) {
    return true;
  }
  return false;
};

// Request interceptor: attach geolocation for profile requests
// The JWT is sent automatically as an httpOnly cookie — no manual header injection needed.
api.interceptors.request.use(
  async (config) => {
    const url = config.url || '';

    // Add location to profile/me requests
    if (url.includes('/users/profile/me')) {
      try {
        const location = await getCurrentLocation();
        if (location) {
          config.params = config.params || {};
          config.params.latitude = location.latitude;
          config.params.longitude = location.longitude;
        }
      } catch (error) {
        console.warn('Failed to get location for profile request:', error);
      }
    }

    // Don't set Content-Type for FormData - let axios set it automatically with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: handle 401 globally
// Rotated tokens arrive as an updated httpOnly cookie — no client-side action needed.
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    if (error.response && error.response.status === 429) {
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        'Too many requests. Please wait a moment and try again.';
      toast.error(message);
    }

    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';

      // Don't redirect for public endpoints (like QuickBooks auth)
      if (!shouldSkipAuth(url)) {
        // Only reload if a session was previously confirmed. If isTokenValid() is
        // already false the app is already on the login screen — reloading would
        // cause an infinite loop (startup auth-check 401 → reload → repeat).
        const wasActive = isTokenValid();
        clearAuth();
        if (wasActive && typeof window !== 'undefined') {
          window.location.reload();
        }
      }
    }
    return Promise.reject(error);
  }
);

// Generic API request wrapper (keeps same signature as before but returns axios response)
export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const method = (options.method || 'GET').toLowerCase() as
    | 'get'
    | 'post'
    | 'put'
    | 'delete'
    | 'patch';

  const config: AxiosRequestConfig = {
    url: endpoint,
    method,
    data: options.body ? JSON.parse(options.body as string) : undefined,
    headers: options.headers as any,
  };

  return api.request(config);
};

// Login API call
export const login = async (email: string, password: string) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  } catch (err) {
    const error = err as AxiosError<any>;
    const message =
      error.response?.data?.message ||
      (error.response?.data as any)?.error ||
      'Login failed';
    throw new Error(message);
  }
};

// Forgot password API call
export const forgotPassword = async (email: string) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  } catch (err) {
    const error = err as AxiosError<any>;
    const message = error.response?.data?.message || 'Request failed';
    throw new Error(message);
  }
};

// Reset password API call
export const resetPassword = async (token: string, password: string) => {
  try {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  } catch (err) {
    const error = err as AxiosError<any>;
    const message = error.response?.data?.message || 'Reset failed';
    throw new Error(message);
  }
};

// Logout API call — clears the httpOnly auth_token cookie on the server
export const logout = async (): Promise<void> => {
  try {
    await api.post('/auth/logout');
  } catch {
    // Ignore errors — we still clear local state regardless
  }
};

// Signup API call
export const signup = async (email: string, password: string, name?: string, phone?: string, recaptchaToken?: string) => {
  try {
    const response = await api.post('/auth/register', { email, password, name, phone, recaptchaToken });
    return response.data;
  } catch (err) {
    const error = err as AxiosError<any>;
    const message =
      error.response?.data?.message ||
      (error.response?.data as any)?.error ||
      'Sign up failed';
    throw new Error(message);
  }
};